/**
 * Order State Machine
 * P20250925-111 — Escrow Webhooks + Order State Machine
 *
 * Defines valid state transitions and idempotent state updates
 */

import { OrderState, OrderStateHistory } from '@/types/order';

/**
 * Valid state transitions
 * Each state can only move to specific next states
 */
const VALID_TRANSITIONS: Record<OrderState, OrderState[]> = {
  created: ['in_progress', 'canceled', 'failed'],
  in_progress: ['paid', 'canceled', 'failed'],
  paid: ['secured', 'canceled', 'failed'],
  secured: ['transferred', 'canceled', 'failed'],
  transferred: ['closed', 'failed'],
  closed: [], // Terminal state
  canceled: [], // Terminal state
  failed: [], // Terminal state
};

/**
 * Check if a state transition is valid
 */
export function isValidTransition(from: OrderState, to: OrderState): boolean {
  // Allow same state (idempotent)
  if (from === to) return true;

  const validNext = VALID_TRANSITIONS[from] || [];
  return validNext.includes(to);
}

/**
 * Get the next state based on Escrow webhook event type
 */
export function getStateFromEscrowEvent(
  eventType: string,
  currentState: OrderState,
): OrderState | null {
  const eventMap: Record<string, OrderState> = {
    'transaction.created': 'in_progress',
    'transaction.updated': 'in_progress',
    'transaction.funded': 'paid',
    'transaction.secured': 'secured',
    'transaction.disbursed': 'transferred',
    'transaction.completed': 'closed',
    'transaction.cancelled': 'canceled',
    'transaction.canceled': 'canceled',
    'transaction.failed': 'failed',
  };

  const newState = eventMap[eventType.toLowerCase()];

  if (!newState) return null;
  if (!isValidTransition(currentState, newState)) {
    console.warn(`[Orders] Invalid transition from ${currentState} to ${newState}`);
    return null;
  }

  return newState;
}

/**
 * Create a new state history entry
 */
export function createStateHistoryEntry(
  state: OrderState,
  source: 'webhook' | 'manual' | 'system' = 'system',
): OrderStateHistory {
  return {
    state,
    timestamp: new Date().toISOString(),
    source,
  };
}

/**
 * Check if an order is in a terminal state
 */
export function isTerminalState(state: OrderState): boolean {
  return ['closed', 'canceled', 'failed'].includes(state);
}

/**
 * Get human-readable state label
 */
export function getStateLabel(state: OrderState): string {
  const labels: Record<OrderState, string> = {
    created: 'Created',
    in_progress: 'In Progress',
    paid: 'Paid',
    secured: 'Secured',
    transferred: 'Transferred',
    closed: 'Completed',
    canceled: 'Canceled',
    failed: 'Failed',
  };

  return labels[state] || state;
}

/**
 * Get badge variant for state
 */
export function getStateBadgeVariant(
  state: OrderState,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  const variants: Record<OrderState, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    created: 'outline',
    in_progress: 'secondary',
    paid: 'default',
    secured: 'default',
    transferred: 'default',
    closed: 'default',
    canceled: 'destructive',
    failed: 'destructive',
  };

  return variants[state] || 'outline';
}
